import React from "react";
import cx from "classnames";
import t from "../i18n";
import { ArrowLeftSLine, ArrowRightSLine } from "../IconV2/react";
import { css } from "@linaria/core";
import { colors, fonts, spacings } from "../Theme";
import Text from "../Text";
import ButtonIconV2 from "../ButtonIconV2";

export function PageSizeSelector({
  options = [20, 50, 100, 200],
  currentSelectionValue = 50,
  onPageSizeChanged,
}: {
  currentSelectionValue?: number;
  options?: number[];
  onPageSizeChanged: (value: number) => void;
}) {
  return (
    <div className={pageSizeSelector}>
      {options.map(option => (
        <button
          role="button"
          className={cx(pageSizeValue, { active: currentSelectionValue === option })}
          key={option}
          onClick={() => onPageSizeChanged(option)}
        >
          {option}
        </button>
      ))}
      <Text type="body2SemiBold" className={pageSizeLabel}>
        {t("table.pagination.elementByPage")}
      </Text>
    </div>
  );
}

const pageSizeSelector = css`
  display: flex;
  align-items: center;
  gap: ${spacings.m};
  padding: ${spacings.xxs};
  color: ${colors.carbon800};
`;

const pageSizeValue = css`
  all: unset;
  font: ${fonts.body2};
  color: ${colors.carbon600};
  cursor: pointer;
  transition: color 100ms linear;

  &:hover,
  &.active {
    color: ${colors.carbon800};
  }

  &.active {
    font: ${fonts.body2SemiBold};
  }
`;

const pageSizeLabel = css`
  margin-left: ${spacings.xl};
`;

export function PageSwitcher({
  pageIndex,
  pageSize,
  elementNumber,
  onPageChanged,
}: {
  pageIndex: number;
  pageSize: number;
  elementNumber: number;
  onPageChanged: (value: number) => void;
}) {
  const startingIndex = pageIndex * pageSize;
  const nextIndex = startingIndex + pageSize;

  const isPreviousActive = pageIndex > 0;
  const isNextActive = nextIndex <= elementNumber;
  const maxIndex = Math.min(nextIndex, elementNumber);

  return (
    <div className={pageSwitcher}>
      <Text type="body2" className={pageSwitcherLabel}>
        {startingIndex + 1} - {maxIndex} {t("table.pagination.of")} {elementNumber}
      </Text>
      <ButtonIconV2
        disabled={!isPreviousActive}
        onClick={() => (isPreviousActive ? onPageChanged(pageIndex - 1) : undefined)}
      >
        <ArrowLeftSLine size="s" />
      </ButtonIconV2>
      <ButtonIconV2
        disabled={!isNextActive}
        onClick={() => (isNextActive ? onPageChanged(pageIndex + 1) : undefined)}
      >
        <ArrowRightSLine size="s" />
      </ButtonIconV2>
    </div>
  );
}

const pageSwitcher = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  padding: ${spacings.xxs};
  user-select: none;
`;

const pageSwitcherLabel = css`
  color: ${colors.carbon600};
`;
